import type { PlaceCandidate } from "./types";

const DEFAULT_BASE_URL = "https://nominatim.openstreetmap.org";
const MIN_REQUEST_INTERVAL_MS = 1100;
const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const SEARCH_CACHE_LIMIT = 50;

export interface PlaceSearchContext {
  destination?: string;
  worldwide?: boolean;
}

export interface PlaceSearchResult {
  places: PlaceCandidate[];
  providerId: string;
  fetchedAt: string;
  cached: boolean;
  stale: boolean;
}

export interface PlaceSearchProvider {
  readonly id: string;
  readonly capabilities: {
    placeSearch: true;
    openingHours: false;
    liveRouting: false;
  };
  search: (
    query: string,
    signal?: AbortSignal,
    context?: PlaceSearchContext,
  ) => Promise<PlaceCandidate[]>;
}

interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  name?: string;
  display_name: string;
  category?: string;
  type?: string;
}

export interface NominatimReverseResult {
  display_name?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    country?: string;
  };
}

let requestQueue: Promise<void> = Promise.resolve();
let lastRequestAt = 0;
interface SearchCacheEntry {
  places: PlaceCandidate[];
  fetchedAt: string;
  expiresAt: number;
}

const searchCache = new Map<string, SearchCacheEntry>();
const placeDetailsCache = new Map<string, PlaceCandidate>();

function searchCacheKey(query: string, context?: PlaceSearchContext): string {
  const destination = context?.worldwide ? "" : context?.destination?.trim().toLocaleLowerCase() || "";
  return `${query.trim().toLocaleLowerCase()}\u0000${destination}\u0000${Boolean(context?.worldwide)}`;
}

function copyPlaces(places: PlaceCandidate[]): PlaceCandidate[] {
  return places.map((place) => ({ ...place }));
}

function rememberSearch(key: string, places: PlaceCandidate[], fetchedAt: string): void {
  searchCache.delete(key);
  searchCache.set(key, {
    places: copyPlaces(places),
    fetchedAt,
    expiresAt: Date.now() + SEARCH_CACHE_TTL_MS,
  });
  for (const place of places) placeDetailsCache.set(place.id, { ...place });
  while (searchCache.size > SEARCH_CACHE_LIMIT) {
    const oldest = searchCache.keys().next().value;
    if (typeof oldest !== "string") break;
    searchCache.delete(oldest);
  }
}

async function waitForRateLimit(signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  const waitMs = Math.max(0, MIN_REQUEST_INTERVAL_MS - (Date.now() - lastRequestAt));
  if (waitMs === 0) return;

  await new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      reject(new DOMException("Request cancelled", "AbortError"));
    };
    const timeout = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, waitMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function withNominatimRateLimit<T>(request: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  let releaseQueue: () => void = () => undefined;
  const previous = requestQueue;
  requestQueue = new Promise<void>((resolve) => {
    releaseQueue = resolve;
  });
  await previous;

  try {
    await waitForRateLimit(signal);
    lastRequestAt = Date.now();
    return await request();
  } finally {
    releaseQueue();
  }
}

function nominatimBaseUrl(): string {
  return (import.meta.env.VITE_NOMINATIM_BASE_URL || DEFAULT_BASE_URL).replace(/\/$/, "");
}

async function searchNominatim(
  query: string,
  signal?: AbortSignal,
  context?: PlaceSearchContext,
): Promise<PlaceCandidate[]> {
  const cleanQuery = query.trim();
  if (cleanQuery.length < 3) {
    throw new Error("Search needs at least three characters.");
  }
  if (cleanQuery.length > 120) {
    throw new Error("Search is limited to 120 characters.");
  }

  return withNominatimRateLimit(async () => {
    const url = new URL(`${nominatimBaseUrl()}/search`);
    const destination = context?.worldwide ? "" : context?.destination?.trim();
    url.searchParams.set("q", destination ? `${cleanQuery}, ${destination}` : cleanQuery);
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("limit", "6");
    url.searchParams.set("addressdetails", "1");

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Accept-Language": "en",
      },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Place search is unavailable (${response.status}). Try again in a moment.`);
    }

    const results = (await response.json()) as NominatimResult[];
    return results.map((result) => ({
      id: String(result.place_id),
      name: result.name?.trim() || result.display_name.split(",")[0]?.trim() || "Unnamed place",
      displayName: result.display_name,
      lat: Number(result.lat),
      lon: Number(result.lon),
      category: result.category || "place",
      type: result.type || "place",
    }));
  }, signal);
}

export const nominatimPlaceSearchProvider: PlaceSearchProvider = {
  id: "openstreetmap-nominatim",
  capabilities: {
    placeSearch: true,
    openingHours: false,
    liveRouting: false,
  },
  search: searchNominatim,
};

export async function searchPlacesWithMeta(
  query: string,
  signal?: AbortSignal,
  context?: PlaceSearchContext,
  provider: PlaceSearchProvider = nominatimPlaceSearchProvider,
): Promise<PlaceSearchResult> {
  const key = `${provider.id}\u0000${searchCacheKey(query, context)}`;
  const cached = searchCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    searchCache.delete(key);
    searchCache.set(key, cached);
    return {
      places: copyPlaces(cached.places),
      providerId: provider.id,
      fetchedAt: cached.fetchedAt,
      cached: true,
      stale: false,
    };
  }

  try {
    const places = await provider.search(query, signal, context);
    const fetchedAt = new Date().toISOString();
    rememberSearch(key, places, fetchedAt);
    return { places: copyPlaces(places), providerId: provider.id, fetchedAt, cached: false, stale: false };
  } catch (cause) {
    if (signal?.aborted) throw cause;
    if (cached) {
      return {
        places: copyPlaces(cached.places),
        providerId: provider.id,
        fetchedAt: cached.fetchedAt,
        cached: true,
        stale: true,
      };
    }
    throw cause;
  }
}

export async function searchPlaces(
  query: string,
  signal?: AbortSignal,
  context?: PlaceSearchContext,
): Promise<PlaceCandidate[]> {
  return (await searchPlacesWithMeta(query, signal, context)).places;
}

export function getCachedPlaceCandidate(placeId: string): PlaceCandidate | null {
  const place = placeDetailsCache.get(placeId);
  return place ? { ...place } : null;
}

export function clearPlaceSearchCache(): void {
  searchCache.clear();
  placeDetailsCache.clear();
}

export function cityFromReverseResult(result: NominatimReverseResult): string {
  const address = result.address ?? {};
  const cityName = [
    address.city,
    address.town,
    address.village,
    address.municipality,
    address.county,
    address.state,
  ].find((value) => value?.trim())?.trim();

  if (!cityName) {
    throw new Error("We could not match that location to a city. Search and choose one instead.");
  }

  const country = address.country?.trim();
  if (!country || cityName.toLocaleLowerCase() === country.toLocaleLowerCase()) return cityName;
  return `${cityName}, ${country}`;
}

export async function reverseGeocodeCity(latitude: number, longitude: number, signal?: AbortSignal): Promise<string> {
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    throw new Error("The location service returned an invalid latitude.");
  }
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    throw new Error("The location service returned an invalid longitude.");
  }

  return withNominatimRateLimit(async () => {
    const url = new URL(`${nominatimBaseUrl()}/reverse`);
    url.searchParams.set("lat", String(latitude));
    url.searchParams.set("lon", String(longitude));
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("zoom", "10");
    url.searchParams.set("addressdetails", "1");
    url.searchParams.set("layer", "address");

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Accept-Language": "en",
      },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Location lookup is unavailable (${response.status}). Search and choose a city instead.`);
    }

    return cityFromReverseResult((await response.json()) as NominatimReverseResult);
  }, signal);
}
